import { useState } from 'react'

/* Shared label / color / icon lookups for every generatable artifact type. */
const TYPE_LABELS = {
  briefing: 'Research Brief',
  flashcards: 'Flashcards',
  study_guide: 'Study Guide',
  timeline: 'Timeline',
  mind_map: 'Mind Map',
  formulas: 'Formula Sheet',
  quiz: 'Quiz',
  agent: 'Agent Workflow',
  study_plan: 'Study Plan'
}
const TYPE_COLORS = {
  briefing: 'var(--accent-2)',
  flashcards: 'var(--amber)',
  study_guide: 'var(--green)',
  timeline: 'var(--sky)',
  mind_map: 'var(--purple)',
  formulas: 'var(--rose)',
  quiz: 'var(--blue)',
  agent: 'var(--green)',
  study_plan: 'var(--blue)'
}
const TYPE_ICONS = {
  briefing: 'article',
  flashcards: 'style',
  study_guide: 'menu_book',
  timeline: 'timeline',
  mind_map: 'account_tree',
  formulas: 'functions',
  quiz: 'quiz',
  agent: 'smart_toy',
  study_plan: 'calendar_month'
}

const ASSET_CARDS = [
  {
    id: 'briefing',
    label: 'Research Brief',
    sublabel: 'Key topics summarized',
    icon: 'article',
    iconFill: true,
    color: 'var(--accent-2)',
    bg: 'var(--accent-soft)'
  },
  {
    id: 'flashcards',
    label: 'Flashcards',
    sublabel: 'Active recall Q&A',
    icon: 'style',
    iconFill: true,
    color: 'var(--amber)',
    bg: 'var(--amber-soft)'
  },
  {
    id: 'study_guide',
    label: 'Study Guide',
    sublabel: 'Structured notes',
    icon: 'menu_book',
    iconFill: true,
    color: 'var(--green)',
    bg: 'var(--green-soft)'
  },
  {
    id: 'timeline',
    label: 'Timeline',
    sublabel: 'Chronological view',
    icon: 'timeline',
    iconFill: false,
    color: 'var(--sky)',
    bg: 'var(--sky-soft)'
  },
  {
    id: 'mind_map',
    label: 'Mind Map',
    sublabel: 'Visual connections',
    icon: 'account_tree',
    iconFill: true,
    color: 'var(--purple)',
    bg: 'var(--purple-soft)'
  },
  {
    id: 'formulas',
    label: 'Formulas',
    sublabel: 'Key equations',
    icon: 'functions',
    iconFill: false,
    color: 'var(--rose)',
    bg: 'var(--rose-soft)'
  }
]

const WORKFLOW_CARDS = [
  {
    id: 'agent',
    label: 'AI Agent',
    sublabel: 'Full exam prep workflow',
    icon: 'smart_toy',
    iconFill: true,
    color: 'var(--green)',
    bg: 'var(--green-soft)'
  },
  {
    id: 'study_plan',
    label: 'Study Plan',
    sublabel: 'Personalized schedule',
    icon: 'calendar_month',
    iconFill: true,
    color: 'var(--blue)',
    bg: 'var(--blue-soft)'
  }
]

const markdownToHTML = text => {
  if (!text) return ''
  return text
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h2>$1</h2>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^\* (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, m => `<ul>${m}</ul>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^(📌|💡|⚡|🔑)(.+)$/gm, '<div class="callout">$1$2</div>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/^(?!<[hul]|<div|<hr)(.+)$/gm, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
}

/* ── PDF export ─────────────────────────────────────────────────────────────── */
function exportOutputAsPDF (output, typeLabel) {
  const printWindow = window.open('', '_blank')
  if (!printWindow) return

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${typeLabel}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Georgia, serif; font-size: 13px; line-height: 1.75; color: #1a1a2e; padding: 48px 56px; max-width: 800px; margin: 0 auto; }
    .header { border-bottom: 3px solid #6366f1; padding-bottom: 16px; margin-bottom: 28px; }
    .header h1 { font-size: 22px; font-weight: 700; color: #1a1a2e; margin-bottom: 4px; }
    .header .meta { font-size: 11px; color: #6b7280; font-family: 'Courier New', monospace; }
    h2 { font-size: 15px; font-weight: 700; margin: 14px 0 4px; color: #1a1a2e; }
    h3 { font-size: 13.5px; font-weight: 600; margin: 8px 0 3px; color: #1a1a2e; }
    p { margin: 2px 0; color: #374151; }
    ul, ol { margin: 8px 0 10px; padding-left: 24px; }
    li { margin: 4px 0; color: #374151; padding-left: 3px; overflow-wrap: anywhere; }
    code { background: #f3f4f6; padding: 1px 5px; border-radius: 3px; font-family: 'Courier New', monospace; font-size: 11.5px; }
    .callout { background: #eef2ff; border-left: 3px solid #6366f1; padding: 8px 12px; margin: 6px 0; border-radius: 0 4px 4px 0; }
    hr { border: 0; border-top: 1px solid #e5e7eb; margin: 18px 0; height: 0; }
    strong { color: #111827; font-weight: 600; }
    em { color: #6b7280; }
    .footer { margin-top: 40px; padding-top: 14px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; display: flex; justify-content: space-between; }
    @media print { body { padding: 32px 40px; } @page { margin: 20mm; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${typeLabel}</h1>
    <div class="meta">Generated by StudyMind AI · ${new Date().toLocaleDateString(
      'en-IN',
      { year: 'numeric', month: 'long', day: 'numeric' }
    )}</div>
  </div>
  <div>${markdownToHTML(output.content)}</div>
  <div class="footer"><span>StudyMind AI</span><span>${typeLabel}</span></div>
</body>
</html>`

  printWindow.document.write(html)
  printWindow.document.close()
  printWindow.onload = () => {
    printWindow.focus()
    printWindow.print()
  }
}

/* ── GenCard ─────────────────────────────────────────────────────────────────── */
function GenCard ({ card, generating, onGenerate }) {
  const isGenerating = generating === card.id
  const [hovered, setHovered] = useState(false)

  return (
    <button
      onClick={() => onGenerate(card.id)}
      disabled={!!generating}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--surface-2)',
        border: '1px solid',
        borderColor: isGenerating
          ? card.color
          : hovered
          ? 'var(--border-2)'
          : 'var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '12px 10px',
        cursor: generating ? 'wait' : 'pointer',
        transition: 'all var(--transition)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 7,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        opacity: generating && !isGenerating ? 0.5 : 1,
        transform: hovered && !generating ? 'translateY(-1px)' : 'none',
        boxShadow: hovered && !generating ? 'var(--shadow-sm)' : 'none'
      }}
    >
      {isGenerating && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${card.color}, transparent)`,
            animation: 'progressBar 1.4s ease infinite'
          }}
        />
      )}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: isGenerating
            ? card.bg
            : hovered
            ? card.bg
            : 'var(--surface-3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all var(--transition)',
          position: 'relative'
        }}
      >
        {isGenerating ? (
          <div
            className='spinner spinner-sm'
            style={{ borderTopColor: card.color }}
          />
        ) : (
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 17,
              color: hovered || isGenerating ? card.color : 'var(--ink-3)',
              fontVariationSettings: card.iconFill ? "'FILL' 1" : "'FILL' 0",
              transition: 'color var(--transition)'
            }}
          >
            {card.icon}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: isGenerating
            ? card.color
            : hovered
            ? 'var(--ink)'
            : 'var(--ink-2)',
          transition: 'color var(--transition)',
          lineHeight: 1.2
        }}
      >
        {card.label}
      </div>
      <div style={{ fontSize: 10, color: 'var(--ink-3)', lineHeight: 1.3 }}>
        {isGenerating ? 'Generating…' : card.sublabel}
      </div>
    </button>
  )
}

/* ── WorkflowCard ────────────────────────────────────────────────────────────── */
function WorkflowCard ({ card, generating, onGenerate }) {
  const isGenerating = generating === card.id
  const [hovered, setHovered] = useState(false)

  return (
    <button
      onClick={() => onGenerate(card.id)}
      disabled={!!generating}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        background: 'var(--surface-2)',
        border: '1px solid',
        borderColor: isGenerating
          ? card.color
          : hovered
          ? 'var(--border-2)'
          : 'var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: '10px 12px',
        cursor: generating ? 'wait' : 'pointer',
        transition: 'all var(--transition)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
        opacity: generating && !isGenerating ? 0.5 : 1
      }}
    >
      {isGenerating && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${card.color}, transparent)`,
            animation: 'progressBar 1.4s ease infinite'
          }}
        />
      )}
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: isGenerating || hovered ? card.bg : 'var(--surface-3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all var(--transition)'
        }}
      >
        {isGenerating ? (
          <div
            className='spinner spinner-sm'
            style={{ borderTopColor: card.color }}
          />
        ) : (
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 15,
              color: hovered ? card.color : 'var(--ink-3)',
              fontVariationSettings: card.iconFill ? "'FILL' 1" : "'FILL' 0",
              transition: 'color var(--transition)'
            }}
          >
            {card.icon}
          </span>
        )}
      </div>
      <div>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: isGenerating ? card.color : 'var(--ink)',
            lineHeight: 1.3
          }}
        >
          {card.label}
        </div>
        <div style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 1 }}>
          {isGenerating ? 'Processing…' : card.sublabel}
        </div>
      </div>
    </button>
  )
}

/* ── OutputRenderer ──────────────────────────────────────────────────────────── */
function OutputRenderer ({ output }) {
  const [copied, setCopied] = useState(false)
  const [exporting, setExporting] = useState(false)

  const copy = () => {
    navigator.clipboard.writeText(output.content || '')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const typeLabel = TYPE_LABELS[output.type] || output.type
  const typeColor = TYPE_COLORS[output.type] || 'var(--ink-2)'

  const handleExport = () => {
    setExporting(true)
    setTimeout(() => {
      exportOutputAsPDF(output, typeLabel)
      setExporting(false)
    }, 100)
  }

  return (
    <div className='animate-fadeIn'>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          padding: '9px 11px',
          background: 'var(--surface-2)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)'
        }}
      >
        <span
          className='material-symbols-rounded'
          style={{
            fontSize: 16,
            color: typeColor,
            fontVariationSettings: "'FILL' 1"
          }}
        >
          auto_awesome
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--ink)'
          }}
        >
          {typeLabel}
        </span>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          {/* Copy */}
          <button
            onClick={copy}
            className='btn btn-ghost btn-icon-sm'
            title='Copy'
          >
            <span
              className='material-symbols-rounded'
              style={{
                fontSize: 14,
                color: copied ? 'var(--green)' : 'var(--ink-3)'
              }}
            >
              {copied ? 'check' : 'content_copy'}
            </span>
          </button>
          {/* Export PDF */}
          <button
            onClick={handleExport}
            disabled={exporting}
            title='Export as PDF'
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 8px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--green-soft, rgba(34,197,94,0.12))',
              border: '1px solid rgba(34,197,94,0.3)',
              color: 'var(--green, #22c55e)',
              fontSize: 11,
              fontWeight: 600,
              cursor: exporting ? 'wait' : 'pointer',
              transition: 'all var(--transition)'
            }}
          >
            {exporting ? (
              <div
                className='spinner spinner-sm'
                style={{
                  borderTopColor: 'var(--green)',
                  borderColor: 'rgba(34,197,94,0.2)',
                  width: 10,
                  height: 10
                }}
              />
            ) : (
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 13, fontVariationSettings: "'FILL' 1" }}
              >
                picture_as_pdf
              </span>
            )}
            PDF
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: '12px 13px',
          fontSize: 12.5,
          lineHeight: 1.75,
          color: 'var(--ink-2)',
          maxHeight: 600,
          overflowY: 'auto'
        }}
        className='md-content'
        dangerouslySetInnerHTML={{ __html: markdownToHTML(output.content) }}
      />
    </div>
  )
}

/* ── GeneratePanel (main export) ─────────────────────────────────────────────── */
export default function GeneratePanel ({
  notebook,
  generating,
  onGenerate,
  onAgent,
  onStudyPlan,
  outputs = [],
  tutorMode,
  setTutorMode,
  socraticMode,
  setSocraticMode,
  quizCount,
  setQuizCount
}) {
  const [panelTab, setPanelTab] = useState('generate')
  const [selectedId, setSelectedId] = useState(null)
  const [prevCount, setPrevCount] = useState(outputs.length)
  const hasOutput = outputs.length > 0

  /* When a new artifact finishes generating, jump to the Output tab and show it.
   * Adjusting state during render is React's recommended pattern for reacting to
   * a changed prop (no effect needed). */
  if (outputs.length !== prevCount) {
    setPrevCount(outputs.length)
    if (outputs.length > prevCount) {
      setSelectedId(outputs[outputs.length - 1].id)
      setPanelTab('output')
    }
  }

  const selectedOutput =
    outputs.find(o => o.id === selectedId) ||
    outputs[outputs.length - 1] ||
    null

  const handleGenerate = id => {
    if (!notebook) return
    if (id === 'agent') {
      onAgent?.()
      return
    }
    if (id === 'study_plan') {
      onStudyPlan?.()
      return
    }
    onGenerate(id)
  }

  return (
    <aside
      style={{
        width: 'var(--panel-w)',
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0
      }}
    >
      {/* Panel tabs */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0
        }}
      >
        {[
          { id: 'generate', icon: 'auto_awesome', label: 'Generate' },
          {
            id: 'output',
            icon: 'description',
            label: 'Output',
            dot: hasOutput
          },
          { id: 'settings', icon: 'tune', label: 'Settings' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setPanelTab(tab.id)}
            style={{
              flex: 1,
              padding: '10px 4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              fontSize: 10.5,
              fontWeight: 500,
              color: panelTab === tab.id ? 'var(--accent-2)' : 'var(--ink-3)',
              background: 'none',
              border: 'none',
              borderBottom: '2px solid',
              borderBottomColor:
                panelTab === tab.id ? 'var(--accent)' : 'transparent',
              transition: 'all var(--transition)',
              position: 'relative',
              cursor: 'pointer'
            }}
          >
            <div style={{ position: 'relative' }}>
              {tab.dot && (
                <span
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -4,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'var(--accent)'
                  }}
                />
              )}
              {tab.id === 'generate' && generating && (
                <span
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -4,
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: 'var(--amber)',
                    animation: 'ping 1s cubic-bezier(0,0,0.2,1) infinite'
                  }}
                />
              )}
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 18,
                  fontVariationSettings:
                    panelTab === tab.id ? "'FILL' 1" : "'FILL' 0"
                }}
              >
                {tab.icon}
              </span>
            </div>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Generate tab ── */}
      {panelTab === 'generate' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
          {!notebook && (
            <div
              style={{
                padding: '16px 12px',
                textAlign: 'center',
                background: 'var(--amber-soft)',
                border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 'var(--radius-lg)',
                marginBottom: 12
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 18,
                  color: 'var(--amber)',
                  fontVariationSettings: "'FILL' 1"
                }}
              >
                info
              </span>
              <div
                style={{
                  fontSize: 11.5,
                  color: 'var(--amber)',
                  marginTop: 4,
                  fontWeight: 500
                }}
              >
                Select a notebook first
              </div>
            </div>
          )}

          {generating && (
            <div
              className='ai-indicator animate-fadeIn'
              style={{
                width: '100%',
                justifyContent: 'center',
                marginBottom: 12,
                borderRadius: 'var(--radius-md)'
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{ fontSize: 15, fontVariationSettings: "'FILL' 1" }}
              >
                auto_awesome
              </span>
              Generating {generating.replace('_', ' ')}…
              <div className='dots'>
                <div className='dot' />
                <div className='dot' />
                <div className='dot' />
              </div>
            </div>
          )}

          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8
            }}
          >
            Study Assets
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 7,
              marginBottom: 12
            }}
          >
            {ASSET_CARDS.map(card => (
              <GenCard
                key={card.id}
                card={card}
                generating={generating}
                onGenerate={handleGenerate}
              />
            ))}
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 8
            }}
          >
            Workflows
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {WORKFLOW_CARDS.map(card => (
              <WorkflowCard
                key={card.id}
                card={card}
                generating={generating}
                onGenerate={handleGenerate}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Output tab ── */}
      {panelTab === 'output' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 10px' }}>
          {!hasOutput ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                gap: 14,
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: 'var(--surface-3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: 24, color: 'var(--ink-3)' }}
                >
                  description
                </span>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--ink-2)',
                    marginBottom: 5
                  }}
                >
                  No output yet
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-3)',
                    lineHeight: 1.5
                  }}
                >
                  Generate study assets from the Generate tab
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* History of generated assets (newest first) */}
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <span>History</span>
                <span
                  style={{
                    color: 'var(--accent-2)',
                    background: 'var(--accent-soft)',
                    padding: '1px 7px',
                    borderRadius: 'var(--radius-full)'
                  }}
                >
                  {outputs.length}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  marginBottom: 12
                }}
              >
                {outputs
                  .slice()
                  .reverse()
                  .map(o => {
                    const active = selectedOutput && o.id === selectedOutput.id
                    const color = TYPE_COLORS[o.type] || 'var(--ink-2)'
                    const label = TYPE_LABELS[o.type] || o.type
                    const time = (() => {
                      try {
                        return new Date(o.ts).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      } catch {
                        return ''
                      }
                    })()
                    return (
                      <button
                        key={o.id}
                        onClick={() => setSelectedId(o.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 9,
                          width: '100%',
                          padding: '7px 9px',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all var(--transition)',
                          background: active
                            ? 'var(--surface-3)'
                            : 'transparent',
                          border: '1px solid',
                          borderColor: active
                            ? 'var(--border-2)'
                            : 'transparent'
                        }}
                      >
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: 7,
                            flexShrink: 0,
                            background: 'var(--surface-3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <span
                            className='material-symbols-rounded'
                            style={{
                              fontSize: 15,
                              color,
                              fontVariationSettings: "'FILL' 1"
                            }}
                          >
                            {TYPE_ICONS[o.type] || 'description'}
                          </span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: active ? 'var(--ink)' : 'var(--ink-2)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {label}
                          </div>
                          {time && (
                            <div
                              style={{
                                fontSize: 10.5,
                                color: 'var(--ink-3)',
                                marginTop: 1
                              }}
                            >
                              {time}
                            </div>
                          )}
                        </div>
                      </button>
                    )
                  })}
              </div>

              {selectedOutput && <OutputRenderer output={selectedOutput} />}
            </>
          )}
        </div>
      )}

      {/* ── Settings tab ── */}
      {panelTab === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 10
            }}
          >
            Tutor Mode
          </div>

          {['beginner', 'student', 'interview', 'professor'].map(mode => (
            <button
              key={mode}
              onClick={() => setTutorMode?.(mode)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid',
                borderColor:
                  tutorMode === mode ? 'rgba(99,102,241,0.3)' : 'var(--border)',
                background:
                  tutorMode === mode ? 'var(--accent-soft)' : 'transparent',
                cursor: 'pointer',
                marginBottom: 5,
                transition: 'all var(--transition)',
                color: tutorMode === mode ? 'var(--accent-2)' : 'var(--ink-2)',
                fontSize: 13,
                fontWeight: tutorMode === mode ? 600 : 400
              }}
            >
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 16,
                  fontVariationSettings:
                    tutorMode === mode ? "'FILL' 1" : "'FILL' 0",
                  color: tutorMode === mode ? 'var(--accent-2)' : 'var(--ink-3)'
                }}
              >
                {mode === 'beginner'
                  ? 'school'
                  : mode === 'student'
                  ? 'person'
                  : mode === 'interview'
                  ? 'work'
                  : 'psychology'}
              </span>
              <span style={{ textTransform: 'capitalize' }}>{mode}</span>
              {tutorMode === mode && (
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 16,
                    marginLeft: 'auto',
                    color: 'var(--accent-2)'
                  }}
                >
                  check_circle
                </span>
              )}
            </button>
          ))}

          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 10,
              marginTop: 18
            }}
          >
            Options
          </div>

          {/* Socratic toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--surface-2)',
              marginBottom: 8
            }}
          >
            <div>
              <div
                style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}
              >
                Socratic Mode
              </div>
              <div
                style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}
              >
                AI guides with questions
              </div>
            </div>
            <button
              onClick={() => setSocraticMode?.(s => !s)}
              style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                padding: 2,
                background: socraticMode ? 'var(--accent)' : 'var(--surface-3)',
                border: '1px solid',
                borderColor: socraticMode ? 'var(--accent)' : 'var(--border-2)',
                cursor: 'pointer',
                transition: 'all var(--transition)',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: 'white',
                  transform: socraticMode
                    ? 'translateX(18px)'
                    : 'translateX(0)',
                  transition: 'transform 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}
              />
            </button>
          </div>

          {/* Quiz count */}
          <div
            style={{
              padding: '9px 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--surface-2)'
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}
                >
                  Quiz Questions
                </div>
                <div
                  style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}
                >
                  Per quiz generation
                </div>
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--accent-2)',
                  background: 'var(--accent-soft)',
                  padding: '1px 8px',
                  borderRadius: 'var(--radius-full)'
                }}
              >
                {quizCount}
              </span>
            </div>
            <input
              type='range'
              min={3}
              max={20}
              value={quizCount}
              onChange={e => setQuizCount?.(+e.target.value)}
              style={{
                width: '100%',
                accentColor: 'var(--accent)',
                background: 'transparent',
                cursor: 'pointer',
                height: 4
              }}
            />
          </div>
        </div>
      )}
    </aside>
  )
}
